import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UtensilsCrossed, ArrowLeft, Coffee, MapPin, Star, ShoppingBasket, Heart, CheckCircle2, RefreshCw } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";
import DiscussionBoard from "../components/DiscussionBoard";
import AskCommunityCTA from "../components/AskCommunityCTA";

const TYPE_LABELS = {
  restaurant: "Restaurants", cafe: "Cafés", bakery: "Bakeries", street_food: "Street Food",
  market: "Markets", food_hall: "Food Halls", food_tour: "Food Tours",
  cooking_class: "Cooking Classes", grocery: "Groceries", farmers_market: "Farmers Markets",
};
const QF_LABELS = [["currency", "Currency"], ["budget_meal", "Budget meal"], ["coffee", "Coffee"],
  ["dinner_time", "Dinner time"], ["tipping", "Tipping"], ["tap_water", "Tap water"],
  ["vegetarian", "Vegetarian"], ["reservations", "Reservations"], ["specialty", "Local specialty"], ["market", "Main market"]];

const Md = ({ children }) => <div className="prose-sm max-w-none text-zinc-700 [&_h3]:font-heading [&_h3]:font-900 [&_ul]:list-disc [&_ul]:pl-5 space-y-2"><ReactMarkdown>{children}</ReactMarkdown></div>;
const H2 = ({ children }) => <h2 className="font-heading font-900 text-2xl text-orange-700 mt-10 mb-3">{children}</h2>;

export default function FoodDining() {
  const { "*": slug } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [saves, setSaves] = useState({}); // place_id -> want|ate

  const load = useCallback(() => api.get(`/food/${slug}`).then((r) => setP(r.data)).catch(() => setP({ status: "FAILED" })), [slug]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!p || !["QUEUED", "GENERATING"].includes(p.status)) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [p, load]);
  useEffect(() => {
    if (!user) return;
    api.get("/food/saves").then((r) => {
      setSaves(Object.fromEntries((r.data.items || []).map((x) => [x.id, x.state])));
    }).catch(() => {});
  }, [user]);

  const toggleSave = async (placeId, state) => {
    if (!user) { toast.error("Sign in to build your food list"); return; }
    try {
      const r = await api.post("/food/saves", { place_id: placeId, state });
      setSaves((s) => ({ ...s, [placeId]: r.data.state }));
      if (r.data.state === "want") toast.success("Added to your Want to Try list");
      else if (r.data.state === "ate") toast.success("Marked as Ate Here");
    } catch { toast.error("Could not save"); }
  };

  if (!p) return <Spinner full />;
  if (["QUEUED", "GENERATING"].includes(p.status)) return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="food-generating">
      <RefreshCw className="w-10 h-10 mx-auto text-orange-500 animate-spin" />
      <h1 className="font-heading font-900 text-2xl mt-4">Collecting Food &amp; Dining intelligence about {p.name || "this destination"}…</h1>
      <p className="text-sm text-muted-foreground mt-2">Signature dishes, markets, tipping, costs, tap water and more. This usually takes under a minute — the page refreshes automatically.</p>
    </div>
  );
  if (p.status !== "PUBLISHED") return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="food-failed">
      <h1 className="font-heading font-900 text-2xl">We couldn't build this food guide yet</h1>
      <button onClick={load} className="mt-4 px-4 py-2 text-sm font-700 text-white bg-orange-600 hover:bg-orange-700">Try again</button>
    </div>
  );

  const d = p.data || {};
  const byType = {};
  (p.places || []).forEach((pl) => (byType[pl.type] = byType[pl.type] || []).push(pl));

  return (
    <div data-testid="food-dining-page">
      <section className="text-white" style={{ backgroundColor: "#7c2d12" }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12">
          <Link to={`/explore/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-orange-200 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> {p.name} guide</Link>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl flex items-center gap-3" data-testid="food-title">
            <UtensilsCrossed className="w-10 h-10" /> Food &amp; Dining in {p.name}
          </h1>
          <p className="text-orange-200 mt-2 text-sm">What to eat, where to eat, what it costs — and how to eat like a local.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        {/* Quick facts */}
        {d.quick_facts && (
          <div className="border-2 border-orange-200 bg-orange-50/60 p-5 mb-8" data-testid="food-quick-facts">
            <div className="font-heading font-900 text-sm uppercase tracking-wide text-orange-800 mb-3">Food &amp; Dining Quick Facts</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              {QF_LABELS.filter(([k]) => d.quick_facts[k]).map(([k, l]) => (
                <div key={k}><span className="font-700">{l}:</span> <span className="text-zinc-700">{d.quick_facts[k]}</span></div>
              ))}
            </div>
          </div>
        )}

        {/* 5 foods */}
        {d.five_foods?.length > 0 && (
          <div className="border border-zinc-300 bg-white p-5 mb-8" data-testid="food-five">
            <div className="font-heading font-900 text-lg mb-2 flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> 5 Foods to Know in {p.name}</div>
            <ol className="list-decimal pl-5 space-y-1 text-sm">
              {d.five_foods.map((f, i) => <li key={i}><b>{f.name}</b> — {f.why}</li>)}
            </ol>
          </div>
        )}

        {d.identity && <><H2>A Taste of {p.name}</H2><Md>{d.identity}</Md></>}

        {d.signature_dishes?.length > 0 && (
          <>
            <H2>Foods You Should Know</H2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="food-dishes">
              {d.signature_dishes.map((s, i) => (
                <div key={i} className="border border-zinc-200 bg-white p-3.5">
                  <div className="font-700">{s.name}{s.local_name && s.local_name !== s.name && <span className="text-muted-foreground font-400 notranslate"> · {s.local_name}</span>}</div>
                  <div className="flex flex-wrap gap-1.5 my-1 text-[11px]">
                    {s.meal && <span className="bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded capitalize">{s.meal}</span>}
                    {s.veg && <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">Vegetarian</span>}
                    {s.spice && s.spice !== "none" && <span className="bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded capitalize">🌶 {s.spice}</span>}
                  </div>
                  <p className="text-sm text-zinc-700">{s.description}</p>
                  {s.where && <p className="text-xs text-muted-foreground mt-1">Where: {s.where}</p>}
                </div>
              ))}
            </div>
          </>
        )}

        {d.street_food && <><H2>Street Food &amp; Quick Eats</H2><Md>{d.street_food}</Md></>}

        {d.neighborhoods?.length > 0 && (
          <>
            <H2>Where to Eat</H2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="food-neighborhoods">
              {d.neighborhoods.map((n, i) => (
                <div key={i} className="border border-zinc-200 bg-white p-3.5">
                  <div className="font-700 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-orange-600" /> {n.name}</div>
                  <p className="text-sm text-zinc-700 mt-1">{n.known_for}</p>
                  {n.price_level && <span className="text-[11px] text-muted-foreground capitalize">{n.price_level}</span>}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Places by type with Want to Try / Ate Here */}
        {Object.keys(byType).length > 0 && (
          <>
            <H2>Places Travelers Talk About</H2>
            <p className="text-xs text-muted-foreground -mt-2 mb-4">AI-researched from well-known establishments — <b>always verify current status and hours before visiting</b>. Nothing here is a paid placement.</p>
            {Object.entries(TYPE_LABELS).filter(([t]) => byType[t]?.length).map(([t, label]) => (
              <div key={t} className="mb-5" data-testid={`food-places-${t}`}>
                <h3 className="font-heading font-900 text-lg mb-2 flex items-center gap-2">
                  {t === "market" || t === "farmers_market" ? <ShoppingBasket className="w-4 h-4 text-orange-600" /> : t === "cafe" ? <Coffee className="w-4 h-4 text-orange-600" /> : <UtensilsCrossed className="w-4 h-4 text-orange-600" />}
                  {label}
                </h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {byType[t].map((pl) => (
                    <div key={pl.id} className="border border-zinc-200 bg-white p-3.5" data-testid={`food-place-${pl.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-700">{pl.name}</div>
                        {pl.price_level && <span className="text-[11px] text-muted-foreground capitalize shrink-0">{pl.price_level}</span>}
                      </div>
                      {pl.area && <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {pl.area}</div>}
                      <p className="text-sm text-zinc-700 mt-1">{pl.known_for}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {pl.veg_friendly && <span className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded">Veg friendly</span>}
                        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Verify before visiting</span>
                        <span className="ml-auto flex gap-1.5">
                          <button onClick={() => toggleSave(pl.id, "want")} data-testid={`food-want-${pl.id}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-700 px-2 py-1 border rounded-full transition-colors ${saves[pl.id] === "want" ? "bg-rose-600 text-white border-rose-600" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}>
                            <Heart className="w-3 h-3" /> Want to try
                          </button>
                          <button onClick={() => toggleSave(pl.id, "ate")} data-testid={`food-ate-${pl.id}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-700 px-2 py-1 border rounded-full transition-colors ${saves[pl.id] === "ate" ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                            <CheckCircle2 className="w-3 h-3" /> Ate here
                          </button>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        {d.meal_times && <><H2>Meal Times, Hours &amp; Reservations</H2><Md>{d.meal_times}</Md></>}
        {d.veg_vegan && <><H2>Vegetarian &amp; Vegan</H2><Md>{d.veg_vegan}</Md></>}

        {d.allergy_phrases?.length > 0 && (
          <>
            <H2>Allergy &amp; Dietary Phrases</H2>
            <p className="text-xs text-muted-foreground mb-2">Travelers with serious allergies should always confirm ingredients directly with the establishment.</p>
            <div className="border border-zinc-200 bg-white divide-y" data-testid="food-phrases">
              {d.allergy_phrases.map((ph, i) => (
                <div key={i} className="p-3 text-sm flex flex-wrap gap-x-4">
                  <span className="font-700">{ph.english}</span>
                  <span className="notranslate text-zinc-700">{ph.local}</span>
                  {ph.transliteration && <span className="text-muted-foreground italic notranslate">{ph.transliteration}</span>}
                </div>
              ))}
            </div>
          </>
        )}

        {(d.costs?.length > 0 || d.tipping) && <H2>What Does It Cost — and Tipping</H2>}
        {d.costs?.length > 0 && (
          <div className="border border-zinc-200 bg-white mb-3" data-testid="food-costs">
            {d.costs.map((c, i) => (
              <div key={i} className={`flex justify-between px-4 py-2 text-sm ${i % 2 ? "bg-zinc-50" : ""}`}>
                <span>{c.item}</span><span className="font-mono">{c.range}</span>
              </div>
            ))}
            <div className="px-4 py-1.5 text-[10px] text-muted-foreground border-t">Typical observed ranges — prices vary by season and venue.</div>
          </div>
        )}
        {d.tipping && <Md>{d.tipping}</Md>}

        {(d.tap_water || d.food_safety) && (
          <>
            <H2>Food &amp; Water Safety</H2>
            {d.tap_water && <p className="text-sm font-700 bg-sky-50 border border-sky-200 p-3 mb-2" data-testid="food-tap-water">💧 {d.tap_water}</p>}
            {d.food_safety && <Md>{d.food_safety}</Md>}
          </>
        )}

        {d.seasonal && <><H2>What's in Season?</H2><Md>{d.seasonal}</Md></>}

        {d.festivals?.length > 0 && (
          <>
            <H2>Food Festivals</H2>
            <ul className="list-disc pl-5 text-sm space-y-1" data-testid="food-festivals">
              {d.festivals.map((f, i) => <li key={i}><b>{f.name}</b> ({f.month}) — {f.what}</li>)}
            </ul>
          </>
        )}

        {d.souvenirs && <><H2>Food to Take Home</H2><Md>{d.souvenirs}</Md></>}

        {d.one_day?.length > 0 && (
          <>
            <H2>One Day of Eating in {p.name}</H2>
            <div className="border-l-4 border-orange-400 pl-4 space-y-2" data-testid="food-one-day">
              {d.one_day.map((o, i) => (
                <div key={i} className="text-sm"><span className="font-700 text-orange-800">{o.slot}:</span> {o.suggestion}</div>
              ))}
            </div>
          </>
        )}

        {d.faqs?.length > 0 && (
          <>
            <H2>Frequently Asked Questions</H2>
            <div className="space-y-3" data-testid="food-faqs">
              {d.faqs.map((f, i) => (
                <div key={i}><div className="font-700 text-sm">{f.q}</div><p className="text-sm text-zinc-700">{f.a}</p></div>
              ))}
            </div>
          </>
        )}

        <div className="border border-orange-300 bg-orange-50/60 p-4 mt-8 flex flex-wrap items-center gap-3" data-testid="food-submit-cta">
          <div className="text-sm flex-1 min-w-[220px]"><b>Run a restaurant, café or food business{p.name ? ` in ${p.name}` : ""}?</b> Or found a great spot? Submit it for review — approved listings appear in this guide.</div>
          <Link to="/submit-business?kind=food" data-testid="food-submit-link"
            className="px-4 py-2 text-sm font-700 text-white bg-orange-600 hover:bg-orange-700">Submit a food business</Link>
        </div>

        <p className="text-[11px] text-muted-foreground mt-8">
          AI-researched guide, generated {p.generated_at?.slice(0, 10)}. Restaurant status, hours and prices change — verify locally before relying on specifics.
        </p>

        <div className="mt-10 space-y-6">
          <AskCommunityCTA entityName={`Food & Dining in ${p.name}`} targetId="food-discussion" />
          <div id="food-discussion" className="scroll-mt-24" data-testid="food-discussion">
            <DiscussionBoard entityType="food" entityId={slug} entityName={`${p.name} Food & Dining`}
              placeholder={`Share a restaurant find, food tip or question about ${p.name}…`} />
          </div>
        </div>
      </div>
    </div>
  );
}
